import type { FC } from 'react'
import CareKeyRelatedView from './CareKeyRelatedView'
import { FormSubmissionRelatedView } from '~/features/member-profile/overview/forms'
import { SteerageRelatedView } from '~/features/member-profile/care-navigation/steerage-view'
import { CareKey, Steerage } from '~/entities/steerage'
import { PriorAuthorizationRelatedView } from '~/features/member-profile/care-navigation/prior-auth'
import MeasureReportRelatedView from './MeasureReportRelatedView'
import { OnboardingRelatedView } from '~/features/member-profile/overview/onboarding'

// Minimal type for FormSubmission - only includes fields needed for related views
type FormSubmission = {
  id: number
  completed_at: string | null
}

// ModelNameToType maps a model's content type name to a minimal type needed to render its components.
type ModelNameToType = {
  formsubmission: Pick<FormSubmission, 'id' | 'completed_at'> & { user_id: number }
  priorauthorization: { pk: number; updated_at: string | null }
  steerage: Pick<Steerage, 'id'>
  waiver: Pick<CareKey, 'pk'>
  measurereport: { id: number }
  onboardingstate: { id: number }
  appointment: { id: number }
  scheduleingestionjob: { id: number }
  adtemergencyencounter: { id: number }
}

export type ModelName = keyof ModelNameToType
type ModelType = ModelNameToType[ModelName]

type ModelRelatedComponent<T = ModelType> = FC<{ model: T }>

type ModelMetadata<T = ModelType> = {
  // Describes the view of a model that should be rendered in the context of another related model's list view.
  related: {
    // Builds the component to render the model's view.
    // Implementation note: The component should take all props necessary to render the item and not fetch data itself,
    // to avoid inefficient queries in a loop.
    component: () => ModelRelatedComponent<T>
  }
}

// Use this component as a temporary placeholder for new relations.
const _EmptyRelatedView = (_props: { model: { id: number } }) => <></>

const MODEL_METADATA: { [key in ModelName]: ModelMetadata<ModelNameToType[key]> } = {
  priorauthorization: {
    related: {
      component: () => PriorAuthorizationRelatedView,
    },
  },
  formsubmission: {
    related: {
      component: () => FormSubmissionRelatedView,
    },
  },
  steerage: {
    related: {
      component: () => SteerageRelatedView,
    },
  },
  waiver: {
    related: {
      component: () => CareKeyRelatedView,
    },
  },
  measurereport: {
    related: {
      component: () => MeasureReportRelatedView,
    },
  },
  onboardingstate: {
    related: {
      component: () => OnboardingRelatedView,
    },
  },
  appointment: {
    related: {
      component: () => _EmptyRelatedView,
    },
  },
  scheduleingestionjob: {
    related: {
      component: () => _EmptyRelatedView,
    },
  },
  adtemergencyencounter: {
    related: {
      component: () => _EmptyRelatedView,
    },
  },
} as const

const metadataFor = (model: ModelName) => MODEL_METADATA[model]

// buildRelatedComponentForModel constructs and returns a React component to render the related view of the model
// having the given content type name.
export const buildRelatedComponentForModel = <T extends ModelName>(
  modelName: T,
): ModelRelatedComponent<ModelNameToType[T]> => {
  const metadata = metadataFor(modelName)
  const { component } = metadata.related

  // Need the type assertion to narrow the type of component returned to just the one for this model name.
  const Component = component() as ModelRelatedComponent<ModelNameToType[T]>

  return Component
}
